import axios from 'axios';
import { readFileSync, writeFileSync } from 'fs';
import { tmpdir } from 'os';
import { join } from 'path';
import { colors } from './ui.js';
import { getPackageVersion, UNRESOLVED_VERSION } from './package-version.js';

// The packument root, not /latest: the abbreviated-packument Accept header below is
// only content-negotiable here, and /latest answers it with HTTP 406.
const REGISTRY_URL = 'https://registry.npmjs.org/@testdino%2fplaywright';
const CHECK_TIMEOUT_MS = 1500;
const PACKAGE_NAME = '@testdino/playwright';
const CHANGELOG_URL = 'https://changelog.testdino.com/?type=cli';

// Without this the registry is hit on every single run — once per shard, per retry,
// per CI job — for a notice that changes at most a few times a year.
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_FILE = join(tmpdir(), 'testdino-playwright-update-check.json');

interface UpdateCache {
  checkedAt: number;
  latest: string;
}

function readCachedLatest(): string | undefined {
  try {
    const parsed = JSON.parse(readFileSync(CACHE_FILE, 'utf-8')) as Partial<UpdateCache>;
    if (typeof parsed.checkedAt !== 'number' || typeof parsed.latest !== 'string') return undefined;
    // A clock moved backwards would otherwise pin a stale entry as fresh forever.
    const age = Date.now() - parsed.checkedAt;
    if (age < 0 || age >= CACHE_TTL_MS) return undefined;
    return parsed.latest;
  } catch {
    return undefined;
  }
}

function writeCachedLatest(latest: string): void {
  try {
    const entry: UpdateCache = { checkedAt: Date.now(), latest };
    writeFileSync(CACHE_FILE, JSON.stringify(entry), 'utf-8');
  } catch {
    // A cache that cannot be written just means we check again next run.
  }
}

// Shared by the CLI and reporter renderers so the two surfaces cannot drift apart.
export function buildUpdateNoticeLines(currentVersion: string, latestVersion: string): string[] {
  // Pad before coloring, since padEnd on a colored string counts invisible ANSI bytes.
  const label = (text: string): string => colors.dim(text.padEnd('Current:'.length));

  return [
    `A new version of ${colors.info(PACKAGE_NAME)} is available`,
    '',
    `${label('Current:')} ${colors.error(currentVersion)}`,
    `${label('Latest:')} ${colors.success(latestVersion)}`,
    '',
    'Update with:',
    `npm install --save-dev ${PACKAGE_NAME}@latest`,
    '',
    `Release notes: ${colors.url(CHANGELOG_URL)}`,
  ];
}

export function compareVersions(a: string, b: string): number {
  const parse = (v: string): number[] =>
    v
      .replace(/^v/, '')
      .split('+')[0]
      .split('-')[0]
      .split('.')
      .map((part) => Number.parseInt(part, 10));

  const left = parse(a);
  const right = parse(b);

  if (left.some(Number.isNaN) || right.some(Number.isNaN)) return 0;

  for (let i = 0; i < 3; i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

// A prerelease (2.1.0-beta.1) must not be told it's behind the stable it precedes.
function isPrerelease(version: string): boolean {
  return version.includes('-');
}

export async function fetchLatestVersion(): Promise<string | undefined> {
  try {
    const response = await axios.get(REGISTRY_URL, {
      timeout: CHECK_TIMEOUT_MS,
      headers: { Accept: 'application/vnd.npm.install-v1+json' },
    });
    const version = (response.data as { 'dist-tags'?: { latest?: unknown } })?.['dist-tags']?.latest;
    return typeof version === 'string' ? version : undefined;
  } catch {
    return undefined;
  }
}

export async function checkForUpdate(currentVersion: string): Promise<string | undefined> {
  // UNRESOLVED_VERSION means we could not read our own package.json. Comparing it
  // would report "Current: 0.0.0" as a real upgrade on every run.
  if (currentVersion === UNRESOLVED_VERSION) return undefined;
  if (isPrerelease(currentVersion)) return undefined;

  const cached = readCachedLatest();
  const latest = cached ?? (await fetchLatestVersion());
  if (!latest || isPrerelease(latest)) return undefined;
  if (!cached) writeCachedLatest(latest);

  return compareVersions(latest, currentVersion) > 0 ? latest : undefined;
}

// The check runs concurrently with the test run. When the run finishes first,
// the notice is worth at most a brief wait, never the full request timeout,
// which would make a fast suite appear to hang after its results are printed.
const SETTLE_GRACE_MS = 150;

export async function awaitUpdateResult(pending: Promise<string | undefined>): Promise<string | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const grace = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), SETTLE_GRACE_MS);
    timer.unref?.();
  });

  try {
    return await Promise.race([pending.catch(() => undefined), grace]);
  } finally {
    clearTimeout(timer);
  }
}

export { getPackageVersion };
